import { createContext, useContext, type ComponentType, type ReactNode, type SVGProps } from 'react'
import { Link } from 'react-router'
import { cn } from '@/shared/lib/utils'
import { Badge, toneSolidClass, type BadgeTone } from './badge'
import { ChevronRightIcon } from '@/shared/components/icons'
import type { RecordView } from './list-toolbar'

// One row anatomy for every record list. List view aligns fields into columns from
// lg up so values can be compared down the page. Grid view stacks the same fields
// into a card. The view is passed by context so there is only ever one row component.
const desktopGrid =
  'lg:grid lg:grid-cols-[2.5rem_minmax(0,2.1fr)_minmax(0,1.1fr)_minmax(0,1.1fr)_8.5rem_1.25rem] lg:items-center lg:gap-4'

// One motion curve for every hover response on a record, so the lift, the strip
// and the chevron read as a single gesture rather than three separate animations.
export const motion = 'duration-[250ms] ease-[cubic-bezier(0.22,1,0.36,1)]'

// Hover is expressed by elevation, border and direction, never by filling the
// surface. Same principle as the header nav, and it keeps text contrast fixed.
const interactiveSurface = cn(
  'group border-line transition-[transform,box-shadow,border-color] hover:border-navy-200 hover:shadow-raised',
  motion
)

const ViewContext = createContext<RecordView>('list')

// Status coloured edge. Reinforces the badge so status is scannable across a grid.
// Animated with transforms only, which the compositor can handle smoothly, rather
// than by animating width or insets. Decorative: the badge carries the wording.
export function AccentStrip({ tone }: { tone: BadgeTone }) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        'absolute inset-y-3 left-1.5 w-1.5 origin-center scale-x-[0.6] rounded-full transition-transform group-hover:scale-x-100 group-hover:scale-y-[1.06]',
        motion,
        toneSolidClass[tone]
      )}
    />
  )
}

export interface RecordMeta {
  label: string
  value: ReactNode
  numeric?: boolean
  mono?: boolean
}

interface RecordRowProps {
  to: string
  icon?: ComponentType<SVGProps<SVGSVGElement>>
  category?: string
  title: string
  subtitle?: string
  metaA?: RecordMeta
  metaB?: RecordMeta
  statusLabel: string
  statusTone: BadgeTone
  actionLabel?: string
}

// Detail line under the title. The category, the loan type, leads it and is weighted
// so it reads as the record's classifier rather than grey filler, then the remaining
// detail follows. Falls back to just the detail when the category is unknown.
function DetailLine({
  category,
  subtitle,
  className,
}: {
  category?: string
  subtitle?: string
  className?: string
}) {
  if (!category && !subtitle) return null
  return (
    <p className={cn('break-words text-xs', className)}>
      {category && <span className="font-semibold text-navy-600">{category}</span>}
      {category && subtitle && <span aria-hidden="true" className="text-neutral-300"> · </span>}
      {subtitle && <span className="text-neutral-500">{subtitle}</span>}
    </p>
  )
}

// Fixed square slot for the product artwork, deliberately with no tinted container:
// the incoming 3D icon set carries its own colour and would clash with a tinted tile.
// Sized so swapping the placeholder line icons for that art needs no layout change.
function ProductIcon({ icon: Icon }: { icon?: ComponentType<SVGProps<SVGSVGElement>> }) {
  if (!Icon) return null
  return (
    <span className="flex h-10 w-10 shrink-0 items-center justify-center">
      <Icon className="h-7 w-7 text-navy-500" aria-hidden="true" />
    </span>
  )
}

export function RecordList({
  children,
  view = 'list',
  className,
}: {
  children: ReactNode
  view?: RecordView
  className?: string
}) {
  return (
    <ViewContext.Provider value={view}>
      <ul
        className={cn(
          view === 'grid'
            ? 'grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'
            : 'space-y-3',
          className
        )}
      >
        {children}
      </ul>
    </ViewContext.Provider>
  )
}

export function RecordRow(props: RecordRowProps) {
  const view = useContext(ViewContext)
  return view === 'grid' ? <RecordCard {...props} /> : <RecordListRow {...props} />
}

function RecordListRow({
  to,
  icon,
  category,
  title,
  subtitle,
  metaA,
  metaB,
  statusLabel,
  statusTone,
  actionLabel,
}: RecordRowProps) {
  return (
    <li>
      <Link
        to={to}
        className={cn(
          'relative block rounded-2xl border bg-white p-4 pl-5 shadow-card lg:pr-5',
          interactiveSurface,
          desktopGrid
        )}
      >
        <AccentStrip tone={statusTone} />

        <div className="flex items-start gap-3 lg:contents">
          <ProductIcon icon={icon} />

          <div className="min-w-0 flex-1 lg:min-w-0">
            <div className="flex items-start justify-between gap-3 lg:block">
              <p className="min-w-0 break-words text-sm font-bold text-navy-900">{title}</p>
              <span className="lg:hidden">
                <Badge tone={statusTone}>{statusLabel}</Badge>
              </span>
            </div>
            <DetailLine category={category} subtitle={subtitle} className="mt-1 lg:mt-0.5" />
          </div>

          <MetaCell meta={metaA} />
          <MetaCell meta={metaB} />

          <div className="hidden lg:block">
            <Badge tone={statusTone}>{statusLabel}</Badge>
          </div>

          <ChevronRightIcon
            className={cn('hidden h-4 w-4 shrink-0 text-neutral-400 transition-[transform,color] group-hover:translate-x-1 group-hover:text-navy-900 lg:block', motion)}
            aria-hidden="true"
          />
        </div>

        {(metaA || metaB || actionLabel) && (
          <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 lg:hidden">
            {metaA && <MobileMeta meta={metaA} />}
            {metaB && <MobileMeta meta={metaB} />}
            {actionLabel && <ActionPill label={actionLabel} />}
          </div>
        )}
      </Link>
    </li>
  )
}

function RecordCard({
  to,
  icon,
  category,
  title,
  subtitle,
  metaA,
  metaB,
  statusLabel,
  statusTone,
  actionLabel,
}: RecordRowProps) {
  return (
    <li>
      <Link
        to={to}
        className={cn(
          'relative flex h-full flex-col rounded-2xl border bg-white p-4 pl-5 shadow-card hover:-translate-y-0.5',
          interactiveSurface
        )}
      >
        <AccentStrip tone={statusTone} />

        <div className="flex items-start gap-3">
          <ProductIcon icon={icon} />
          <div className="min-w-0 flex-1">
            <p className="min-w-0 break-words text-sm font-bold text-navy-900">{title}</p>
            <DetailLine category={category} subtitle={subtitle} className="mt-1" />
          </div>
        </div>

        {(metaA || metaB) && (
          <dl className="mt-4 grid grid-cols-2 gap-px overflow-hidden rounded-xl bg-neutral-200">
            {[metaA, metaB].filter(Boolean).map((meta) => (
              <div key={(meta as RecordMeta).label} className="bg-white px-3 py-2">
                <dt className="text-[11px] text-neutral-500">{(meta as RecordMeta).label}</dt>
                <dd
                  className={cn(
                    'mt-0.5 break-words text-sm font-semibold text-navy-900',
                    (meta as RecordMeta).numeric && 'tabular-nums',
                    (meta as RecordMeta).mono && 'font-mono text-xs tracking-tight'
                  )}
                >
                  {(meta as RecordMeta).value}
                </dd>
              </div>
            ))}
          </dl>
        )}

        <div className="mt-4 flex items-center justify-between gap-3 pt-1">
          <Badge tone={statusTone}>{statusLabel}</Badge>
          {actionLabel ? (
            <ActionPill label={actionLabel} />
          ) : (
            <ChevronRightIcon
              className={cn('h-4 w-4 shrink-0 text-neutral-400 transition-[transform,color] group-hover:translate-x-1 group-hover:text-navy-900', motion)}
              aria-hidden="true"
            />
          )}
        </div>
      </Link>
    </li>
  )
}

function ActionPill({ label }: { label: string }) {
  return (
    <span className="ml-auto inline-flex items-center gap-1 rounded-full bg-navy-900 px-3 py-1.5 text-[11px] font-bold text-white">
      {label}
      <ChevronRightIcon className="h-3 w-3" aria-hidden="true" />
    </span>
  )
}

function MetaCell({ meta }: { meta?: RecordMeta }) {
  if (!meta) return <span className="hidden lg:block" />
  return (
    <div className="hidden min-w-0 lg:block">
      <p className="text-[11px] text-neutral-500">{meta.label}</p>
      <p
        className={cn(
          'mt-0.5 break-words text-sm font-semibold text-navy-900',
          meta.numeric && 'tabular-nums',
          meta.mono && 'font-mono text-xs tracking-tight'
        )}
      >
        {meta.value}
      </p>
    </div>
  )
}

function MobileMeta({ meta }: { meta: RecordMeta }) {
  return (
    <span className="inline-flex items-baseline gap-1.5 text-xs">
      <span className="text-neutral-500">{meta.label}</span>
      <span
        className={cn(
          'font-semibold text-navy-900',
          meta.numeric && 'tabular-nums',
          meta.mono && 'font-mono tracking-tight'
        )}
      >
        {meta.value}
      </span>
    </span>
  )
}